<template>
  <EditorShell v-bind="{ config, lotteryId, loaded, navigateToHome, optionsReady }">
    <template v-if="config">
      <EditorPlayList v-bind="{ animated, config, store }">
        <template #default="{ play }">
          <EditorDateLotteryPicker v-bind="{ config, lotteryId, play }" />
        </template>
      </EditorPlayList>

      <EditorDeleteAllPlays :visible="canDeleteAllPlays" @confirm="deleteAllPlays" />
    </template>

    <template #control-panel>
      <EditorControlPanel v-bind="{ actions, disabled }" @click="handleAction" />
    </template>
  </EditorShell>
</template>

<script lang="ts" setup>
import type { ControlPanelAction } from '../@types/editor'

import {
  EDITOR_CONTROL_PANEL_ACTION_ID,
  EDITOR_QUICK_PICK_ACTION_ID_PREFIX,
  EDITOR_QUICK_PICK_MIN_QUANTITY,
} from '../constants/editor'
import { useDateLotteryStore } from '../stores/dateLottery'

const { t } = useI18n()

const { config, editorIsReady, lotteryId, navigateToHome, optionsReady, playsReady, store } =
  useEditor(useDateLotteryStore)
const { addLine, addLineDisabled, disabled, scrollToCenteredPlay } = useEditorControlPanel(
  store,
  config,
)
const { applyQuickPickFromQuery, quickPickReady } = useEditorQuickPickQuery(store, config)
const { animated, canDeleteAllPlays, deleteAllPlays } = useDeleteAllPlays(store, config)
const { prefillFirstPlay } = useDateLotteryPrefill(store)
const { triggerBatch } = useEditorQuickPickAnimation()

useEditorMaxPlaysToast(store, config, disabled)

const loaded = computed(() => !!config.value && playsReady.value && quickPickReady.value)

const isFillTarget = computed(() => {
  const currentConfig = config.value
  return !!currentConfig && store.isQuickPickFillTarget(currentConfig)
})

const quickPickQuantities = computed(() => {
  const validPlays = config.value?.quickPick?.validQuickPickPlays ?? []
  return [
    ...new Set([
      validPlays.at(0) ?? EDITOR_QUICK_PICK_MIN_QUANTITY,
      validPlays.at(-1) ?? EDITOR_QUICK_PICK_MIN_QUANTITY,
    ]),
  ]
})

const quickPickActions = computed<ControlPanelAction[]>(() =>
  quickPickQuantities.value.map((quantity) => ({
    ariaLabel: t('editor.controlpanel.button.quickpickmultiple.arialabel', quantity),
    icon:
      quantity === EDITOR_QUICK_PICK_MIN_QUANTITY && isFillTarget.value ? 'flash' : 'flash-plus',
    id: `${EDITOR_QUICK_PICK_ACTION_ID_PREFIX}${quantity}`,
    label:
      quantity === EDITOR_QUICK_PICK_MIN_QUANTITY
        ? t('editor.controlpanel.button.quickpick')
        : String(quantity),
    variant: 'prominent',
  })),
)

const actions = computed<ControlPanelAction[]>(() => [
  {
    disabled: addLineDisabled.value,
    icon: 'plus',
    id: EDITOR_CONTROL_PANEL_ACTION_ID.AddLine,
    label: t('editor.controlpanel.button.addline'),
    variant: 'fade',
  },
  ...quickPickActions.value,
])

const toQuickPickQuantity = (actionId: string): Maybe<number> => {
  if (!actionId.startsWith(EDITOR_QUICK_PICK_ACTION_ID_PREFIX)) return null

  const quantity = Number(actionId.slice(EDITOR_QUICK_PICK_ACTION_ID_PREFIX.length))
  return Number.isInteger(quantity) && quantity > 0 ? quantity : null
}

const handleAction = async (actionId: string) => {
  const currentConfig = config.value
  if (!currentConfig) return

  if (actionId === EDITOR_CONTROL_PANEL_ACTION_ID.AddLine) return addLine()

  const quantity = toQuickPickQuantity(actionId)
  if (!quantity) return

  const playIds = triggerBatch({
    config: currentConfig,
    expandedPlayId: store.expandedPlayId,
    plays: () => store.plays,
    quickPick: () => store.quickPickPlays(currentConfig, quantity),
  })

  await scrollToCenteredPlay(playIds.at(-1) ?? null)
}

onMounted(async () => {
  await editorIsReady
  await applyQuickPickFromQuery()
  await nextTick()

  prefillFirstPlay()
})
</script>
